// ============================================================================
// Instagram Login 2024 OAuth — authorize URL + short-to-long token exchange
// + long-lived token refresh.
//
// Flow summary (Instagram Login, NOT Facebook Login for Instagram):
//   1. `buildAuthorizeUrl(...)` → redirect browser to the IG authorize page.
//      Unlike FB Messenger, IG 2024 uses a dedicated `instagram.com/oauth/`
//      endpoint (not the FB dialog).
//   2. IG returns `?code=...&state=...` to our callback. `exchangeCode(...)`
//      swaps the code for a short-lived IG user token (~1 hour) via
//      `api.instagram.com/oauth/access_token`.
//   3. `extendToInstagramLongLivedToken(...)` swaps the short-lived token for
//      a long-lived one (~60 days) via
//      `graph.instagram.com/access_token?grant_type=ig_exchange_token`.
//   4. `refreshInstagramLongLivedToken(...)` extends an existing long-lived
//      token — same 60 day TTL. Safe to rotate when <7 days remain.
//
// References:
//   - https://developers.facebook.com/docs/instagram-platform/instagram-login
//   - https://developers.facebook.com/docs/instagram-platform/instagram-api-with-instagram-login/business-login
// ============================================================================

// ============================================================================
// Constants
// ============================================================================

/** Instagram Login 2024 authorize endpoint (NOT the FB dialog). */
export const IG_OAUTH_AUTHORIZE_URL = 'https://www.instagram.com/oauth/authorize'

/** Short-lived token exchange endpoint (IG Business Login). */
export const IG_OAUTH_TOKEN_URL = 'https://api.instagram.com/oauth/access_token'

/** IG Graph API base — used for long-lived token exchange + refresh. */
export const IG_GRAPH_API_BASE = 'https://graph.instagram.com'

/**
 * Default scope set for the Instagram DM adapter.
 *
 * - `instagram_business_basic` — read the business IG profile (id, username).
 * - `instagram_business_manage_messages` — send/receive DMs.
 * - `instagram_business_content_publish` — post content (future P7b feature).
 *
 * Exact scope names may vary by IG app version — values here match the
 * 2024-09 scope set Meta documented as the Instagram Login 2024 canonical
 * names. If Meta renames them we bump the seed migration (creating a new
 * migration, never editing in place).
 */
export const INSTAGRAM_DM_DEFAULT_SCOPES = [
  'instagram_business_basic',
  'instagram_business_manage_messages',
  'instagram_business_content_publish',
] as const

// ============================================================================
// buildAuthorizeUrl
// ============================================================================

export interface InstagramAuthorizeInput {
  clientId: string
  redirectUri: string
  state: string
  /** Override the default IG scopes. Defaults to {@link INSTAGRAM_DM_DEFAULT_SCOPES}. */
  scopes?: readonly string[]
}

export interface InstagramAuthorizeResult {
  url: string
  state: string
}

/**
 * Build the Instagram OAuth authorize URL.
 *
 * IG uses `scope=instagram_business_basic,...` (comma-separated, NOT
 * space-separated like standard OAuth 2.0). It also requires
 * `response_type=code` and `client_id` / `redirect_uri` to match exactly
 * what was registered in the Meta App Dashboard.
 *
 * NOTE: This helper is intentionally lighter than the FB Messenger
 * `buildAuthorizeUrl` (which persists a PKCE state row). IG 2024 does not
 * support PKCE yet — the state MUST be generated and persisted by the caller
 * (typically via `services/oauth.service.ts#buildAuthorizeUrl` with a
 * post-processing step that swaps the authorize URL). We expose this raw
 * builder so callers can either chain it or compose their own flow.
 */
export function buildAuthorizeUrl(opts: InstagramAuthorizeInput): InstagramAuthorizeResult {
  assertNonEmpty(opts.clientId, 'clientId')
  assertNonEmpty(opts.redirectUri, 'redirectUri')
  assertNonEmpty(opts.state, 'state')

  const scopes =
    opts.scopes && opts.scopes.length > 0
      ? opts.scopes
      : INSTAGRAM_DM_DEFAULT_SCOPES

  const url = new URL(IG_OAUTH_AUTHORIZE_URL)
  url.searchParams.set('client_id', opts.clientId)
  url.searchParams.set('redirect_uri', opts.redirectUri)
  url.searchParams.set('response_type', 'code')
  url.searchParams.set('state', opts.state)
  // IG expects a COMMA-separated scope list — not spaces like RFC 6749.
  url.searchParams.set('scope', scopes.join(','))

  return { url: url.toString(), state: opts.state }
}

// ============================================================================
// exchangeCode — authorization code → short-lived token
// ============================================================================

export interface ExchangeCodeInput {
  code: string
  clientId: string
  clientSecret: string
  redirectUri: string
  /** Override the token endpoint (tests inject a stub). */
  tokenUrl?: string
}

export interface ShortLivedInstagramToken {
  /** Short-lived IG user access token (~1h). */
  accessToken: string
  /** IGSID of the authorizing user. */
  userId: string
  /** Token type echoed by IG — always `Bearer` for IG Login 2024. */
  tokenType?: string
}

/**
 * Exchange an authorization code for a SHORT-LIVED IG user access token.
 *
 * IG's short-lived tokens live ~1 hour. Callers MUST extend to a long-lived
 * token before persisting (see `extendToInstagramLongLivedToken`).
 *
 * The client secret goes in the POST body (never the URL) so Cloudflare edge
 * logs + proxy traces never see it.
 */
export async function exchangeCode(
  input: ExchangeCodeInput,
  fetchImpl: typeof fetch = fetch,
): Promise<ShortLivedInstagramToken> {
  assertNonEmpty(input.code, 'code')
  assertNonEmpty(input.clientId, 'clientId')
  assertNonEmpty(input.clientSecret, 'clientSecret')
  assertNonEmpty(input.redirectUri, 'redirectUri')

  const endpoint = input.tokenUrl ?? IG_OAUTH_TOKEN_URL
  const params = new URLSearchParams({
    client_id: input.clientId,
    client_secret: input.clientSecret,
    grant_type: 'authorization_code',
    redirect_uri: input.redirectUri,
    code: input.code,
  })

  const response = await fetchImpl(endpoint, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!response.ok) {
    const errText = await safeReadText(response)
    console.error(
      '[connectors/instagram-dm/oauth] exchangeCode failed',
      response.status,
      redactInstagramSecrets(errText).slice(0, 500),
    )
    throw new InstagramDmOAuthError(
      'EXCHANGE_FAILED',
      `provider returned status ${response.status}`,
    )
  }

  const body = (await response.json().catch(() => null)) as
    | { access_token?: string; user_id?: string | number; token_type?: string }
    | null
  if (
    !body ||
    typeof body.access_token !== 'string' ||
    body.access_token.length === 0
  ) {
    throw new InstagramDmOAuthError(
      'EXCHANGE_FAILED',
      'provider did not return access_token',
    )
  }

  const userId =
    typeof body.user_id === 'number'
      ? String(body.user_id)
      : typeof body.user_id === 'string'
        ? body.user_id
        : ''

  return {
    accessToken: body.access_token,
    userId,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
  }
}

// ============================================================================
// extendToInstagramLongLivedToken — short-lived → long-lived (~60 days)
// ============================================================================

export interface ExtendInstagramTokenInput {
  shortLivedToken: string
  clientSecret: string
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

export interface LongLivedInstagramToken {
  accessToken: string
  tokenType?: string
  /** Seconds until expiry — typically ~5,184,000 (~60 days). */
  expiresIn?: number
  /** Absolute expiry ISO 8601 UTC, derived from `expires_in` + call time. */
  expiresAt?: string
}

/**
 * Exchange a short-lived IG token for a long-lived one (~60 days).
 *
 * Endpoint: `GET https://graph.instagram.com/access_token?grant_type=ig_exchange_token&client_secret=<x>&access_token=<y>`.
 *
 * SECURITY: Meta mandates that `client_secret` + `access_token` travel in
 * the URL query string on this endpoint. There is no POST-body or
 * Authorization-header alternative. Cloudflare Logpush captures full
 * request URLs by default, so:
 *   1. This call MUST originate from the Worker runtime only (never
 *      client-side).
 *   2. Operators MUST disable URL capture in Logpush for the
 *      graph.instagram.com destination, OR redact `client_secret=` +
 *      `access_token=` at the log-ingest layer.
 *   3. The code here already swallows the raw URL before any log path
 *      (see `console.error` below which redacts), but a CF-level leak
 *      still requires the operational mitigation above. Runbook: docs/
 *      connectors-operational-security.md (follow-up #248-related).
 *
 * Note: IG REQUIRES the client secret on this endpoint (unlike the FB Graph
 * equivalent which accepts `fb_exchange_token` without it on some app
 * configurations).
 */
export async function extendToInstagramLongLivedToken(
  input: ExtendInstagramTokenInput,
  fetchImpl: typeof fetch = fetch,
  now: Date = new Date(),
): Promise<LongLivedInstagramToken> {
  assertNonEmpty(input.shortLivedToken, 'shortLivedToken')
  assertNonEmpty(input.clientSecret, 'clientSecret')

  const base = input.graphApiBase ?? IG_GRAPH_API_BASE
  const url = new URL(`${base}/access_token`)
  url.searchParams.set('grant_type', 'ig_exchange_token')
  url.searchParams.set('client_secret', input.clientSecret)
  url.searchParams.set('access_token', input.shortLivedToken)

  const response = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })

  if (!response.ok) {
    const errText = await safeReadText(response)
    console.error(
      '[connectors/instagram-dm/oauth] extendToInstagramLongLivedToken failed',
      response.status,
      redactInstagramSecrets(errText).slice(0, 500),
    )
    throw new InstagramDmOAuthError(
      'EXTEND_FAILED',
      `provider returned status ${response.status}`,
    )
  }

  const body = (await response.json().catch(() => null)) as
    | { access_token?: string; token_type?: string; expires_in?: number }
    | null
  if (
    !body ||
    typeof body.access_token !== 'string' ||
    body.access_token.length === 0
  ) {
    throw new InstagramDmOAuthError(
      'EXTEND_FAILED',
      'provider did not return access_token',
    )
  }

  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : undefined
  const expiresAt =
    typeof expiresIn === 'number'
      ? new Date(now.getTime() + expiresIn * 1000).toISOString()
      : undefined

  return {
    accessToken: body.access_token,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
    expiresIn,
    expiresAt,
  }
}

// ============================================================================
// refreshInstagramLongLivedToken — rotate an existing long-lived token
// ============================================================================

export interface RefreshInstagramTokenInput {
  longLivedToken: string
  /** Override the Graph API base URL (tests inject a stub). */
  graphApiBase?: string
}

/**
 * Rotate an existing long-lived IG token. Returns a NEW long-lived token
 * with a fresh 60-day TTL. Safe to call when <7 days remain on the current
 * token.
 *
 * Endpoint: `GET https://graph.instagram.com/refresh_access_token?grant_type=ig_refresh_token&access_token=<y>`.
 *
 * SECURITY: same Meta-mandated-URL-query-param constraint as
 * `extendToInstagramLongLivedToken` — see its doc comment. Operators MUST
 * disable URL capture in Logpush for graph.instagram.com or redact
 * `access_token=` at the log-ingest layer.
 *
 * No client secret is required here — the current long-lived token is its
 * own bearer credential. Call from the Worker runtime only.
 */
export async function refreshInstagramLongLivedToken(
  input: RefreshInstagramTokenInput,
  fetchImpl: typeof fetch = fetch,
  now: Date = new Date(),
): Promise<LongLivedInstagramToken> {
  assertNonEmpty(input.longLivedToken, 'longLivedToken')

  const base = input.graphApiBase ?? IG_GRAPH_API_BASE
  const url = new URL(`${base}/refresh_access_token`)
  url.searchParams.set('grant_type', 'ig_refresh_token')
  url.searchParams.set('access_token', input.longLivedToken)

  const response = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })

  if (!response.ok) {
    const errText = await safeReadText(response)
    console.error(
      '[connectors/instagram-dm/oauth] refreshInstagramLongLivedToken failed',
      response.status,
      redactInstagramSecrets(errText).slice(0, 500),
    )
    throw new InstagramDmOAuthError(
      'REFRESH_FAILED',
      `provider returned status ${response.status}`,
    )
  }

  const body = (await response.json().catch(() => null)) as
    | { access_token?: string; token_type?: string; expires_in?: number }
    | null
  if (
    !body ||
    typeof body.access_token !== 'string' ||
    body.access_token.length === 0
  ) {
    throw new InstagramDmOAuthError(
      'REFRESH_FAILED',
      'provider did not return access_token',
    )
  }

  const expiresIn = typeof body.expires_in === 'number' ? body.expires_in : undefined
  const expiresAt =
    typeof expiresIn === 'number'
      ? new Date(now.getTime() + expiresIn * 1000).toISOString()
      : undefined

  return {
    accessToken: body.access_token,
    tokenType: typeof body.token_type === 'string' ? body.token_type : undefined,
    expiresIn,
    expiresAt,
  }
}

// ============================================================================
// Errors
// ============================================================================

export type InstagramDmOAuthErrorCode =
  | 'EXCHANGE_FAILED'
  | 'EXTEND_FAILED'
  | 'REFRESH_FAILED'
  | 'INVALID_INPUT'

export class InstagramDmOAuthError extends Error {
  constructor(
    public readonly code: InstagramDmOAuthErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'InstagramDmOAuthError'
  }
}

// ============================================================================
// Helpers
// ============================================================================

function assertNonEmpty(value: string | undefined | null, field: string): void {
  if (typeof value !== 'string' || value.length === 0) {
    throw new InstagramDmOAuthError('INVALID_INPUT', `${field} is required`)
  }
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text()
  } catch {
    return ''
  }
}

// ============================================================================
// redactInstagramSecrets — shared sanitiser
// ============================================================================

/**
 * Redact any Meta/IG-shaped secrets from a log string. Exported for reuse in
 * the send / profile files so the sanitisation policy stays in one place.
 *
 * Matches the patterns Meta echoes back in error bodies: URL query params
 * (`access_token=`, `client_secret=`, `ig_exchange_token=`, `ig_refresh_token=`),
 * Bearer header contents, and the common long-token prefixes Meta issues
 * (`EAA...` for user access tokens, `IGQ...` for IG-specific tokens). Any
 * base64-ish run of 40+ chars is also redacted as a catch-all for opaque
 * token payloads we haven't enumerated.
 */
const SECRET_PATTERNS: ReadonlyArray<RegExp> = [
  /(access_token=)[^&\s"']+/gi,
  /(client_secret=)[^&\s"']+/gi,
  /(ig_exchange_token=)[^&\s"']+/gi,
  /(ig_refresh_token=)[^&\s"']+/gi,
  /(fb_exchange_token=)[^&\s"']+/gi,
  /(Bearer\s+)[A-Za-z0-9._\-|]+/gi,
  /\bEAA[A-Za-z0-9_\-]{20,}/g,
  /\bIGQ[A-Za-z0-9_\-]{20,}/g,
]

export function redactInstagramSecrets(raw: string): string {
  let out = raw
  for (const pattern of SECRET_PATTERNS) {
    // The token-prefix patterns (EAA / IGQ) do not carry a capture group, so
    // replace with a literal redaction marker when no `$1` is present.
    if (pattern.source.includes('\\b')) {
      out = out.replace(pattern, 'REDACTED')
    } else {
      out = out.replace(pattern, '$1REDACTED')
    }
  }
  // Catch-all: collapse any remaining base64-ish run of 40+ chars. Kept last
  // so the named patterns take precedence.
  out = out.replace(/[A-Za-z0-9+/=_\-]{40,}/g, 'REDACTED')
  return out
}
